import { useEffect, Fragment } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber, sortTableDateFecha, sortTableDateFechaInicio, sortTableDateFechaFin, sortTableStatusStatus } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { obtenerCliente, limpiarCliente, detallesArchivo, limpiarArchivo, desactivarRenovacionMembresia } from '../../actions/clientes';
import { detallesAhorro, limpiarAhorro } from '../../actions/asesorias';

import GestionarMembresia from './GestionarMembresia';
import EliminarMembresia from './EliminarMembresia';
import NuevoArchivo from './NuevoArchivo';
import EditarArchivo from './EditarArchivo';
import EliminarArchivo from './EliminarArchivo';

const DetallesCliente = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Clientes',
            path: '/clientes'
        },
        {
            activo: true,
            nombre: 'Perfil del cliente',
            path: '/clientes/detalles/'
        }
    ];

    const dispatch = useDispatch();

    const { id } = useParams();

    const { usuario } = useSelector(state => state.auth);
    const { detalles, detalles_archivo } = useSelector(state => state.clientes);
    const { detalles_ahorro } = useSelector(state => state.asesorias);

    /* Cliente */
    const [clienteData, setClienteData] = useState({
        indicadores_generales: {
            asesorias_pendientes: 0,
            asesorias_realizadas: 0,
            asesorias_canceladas: 0,
            ahorro_generado: 0,
            membresia_actual: null
        },
        informacion_personal: {
            _id: null,
            nombre: null,
            nacimiento : null,
            celular: null,
            email: null,
            creacion: null,
            status: false
        },
        informacion_empresarial: {
            nombre_comercial: null, 
            razon_social: null, 
            rfc: null, 
            curp: null, 
            curp: null, 
            tipo_persona: null, 
            regimen_fiscal: null, 
            giro: null, 
            representante_legal: null, 
            puesto: null, 
            domicilio_fiscal: null, 
            telefono: null, 
            email: null, 
            web: null, 
            fecha_constitucion: null, 
            numero_empleados: null, 
            minimo_ingresos_anuales: null, 
            maximo_ingresos_anuales: null
        },
        archivos: [],
        asesorias: [],
        ahorros: [],
        membresias: [],
        cobros: []
    });

    const [membresiaActual, setMembresiaActual] = useState(null);

    /* Archivos */
    const [nuevoArchivo, setNuevoArchivo] = useState({
        cliente: null
    });
    const [editarArchivo, setEditarArchivo] = useState(null);
    const [eliminarArchivo, setEliminarArchivo] = useState(null);

    /* Membresias */
    const [eliminarMembresia, setEliminarMembresia] = useState(null);

    const {indicadores_generales, informacion_personal, informacion_empresarial, archivos, asesorias, ahorros, membresias, cobros} = clienteData;

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const TIPOS_AHORRO = {
        1: 'Comisiones bancarias',
        2: 'Tasa de interés',
        3: 'Seguros',
        4: 'Crédito',
        5: 'Otro'
    };

    const ahorrosColumnas = [
        {
            name: 'Tipo',
            sortable: true,
            selector: row => TIPOS_AHORRO[row.tipo] || 'Sin definir'
        },
        {
            name: 'Banco',
            sortable: true,
            selector: row => row.banco || 'Sin definir',
            grow: 2
        },
        {
            name: 'Monto anual',
            sortable: true,
            selector: row => row.monto_anual != null ? formatNumber(row.monto_anual, 2, '$', '') : 'Sin definir'
        },
        {
            name: 'Descripción',
            sortable: true,
            selector: row => row.descripcion || 'Sin definir',
            grow: 3
        },
        {
            name: 'Fecha',
            sortable: true,
            sortFunction: sortTableDateFecha,
            cell: item => item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'
        },
        {
            name: '',
            grow: 0.5,
            cell: row => {
                return (
                    <div className="text-right w-100">
                        {
                            row.extension &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleVerComprobanteAhorro({ id_asesoria: row.id_asesoria, id_ahorro: row.id_ahorro })}><i className="fas fa-eye text-info"></i></button>
                        }
                    </div>
                );
            }
        }
    ];

    const asesoriasColumnas = [
        {
            name: 'Asesor',
            sortable: true, 
            selector: row => row.asesor?.nombre_completo || 'Sin definir', 
            grow: 2
        },
        {
            name: 'Empresa',
            sortable: true, 
            selector: row => row.empresa || '-', 
            wrap: true,
            grow: 1.5
        },
        {
            name: 'Motivo',
            sortable: true, 
            selector: row => row.motivo || '-', 
            wrap: true,
            grow: 2
        },
        {
            name: 'Inicio',
            sortable: true, 
            sortFunction: sortTableDateFechaInicio, 
            cell: item => item.creacion ? new Date(item.creacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }): 'Sin definir'
        },
        {
            name: 'Finalización',
            sortable: true, 
            sortFunction: sortTableDateFechaFin, 
            cell: item => item.finalizacion ? new Date(item.finalizacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }): 'Sin definir'
        },
        {
            name: 'Estado',
            sortable: true,
            sortFunction: sortTableStatusStatus,
            selector: row => {
                switch(row.status){
                    case 0:
                        return(
                            <h6><Badge pill bg="warning">En curso</Badge></h6>
                        )
                    case 1:
                        return(
                            <h6><Badge pill bg="success">Finalizada</Badge></h6>
                        )
                    default:
                        break;
                }
            }
        }
    ];

    const archivosColumnas = [
        {
            name: 'Nombre',
            sortable: true, 
            grow: 2, 
            selector: row => row.nombre || 'Sin definir'
        },
        {
            name: 'Descripción',
            sortable: true, 
            grow: 2, 
            selector: row => row.descripcion || 'Sin definir'
        },
        {
            name: 'Tipo',
            sortable: true,
            selector: row => row.precargado ? 'Pendiente' : (row.extension ? row.extension.toUpperCase() : 'Sin definir'),
            cell: row => row.precargado
                ? <h6 className="mb-0"><Badge pill bg="warning">Pendiente</Badge></h6>
                : (row.extension ? row.extension.toUpperCase() : 'Sin definir')
        },
        {
            name: 'Fecha de carga',
            sortable: true, 
            sortFunction: sortTableDateFecha, 
            cell: item => item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'
        },
        {
            name: '',
            grow: 1, 
            cell: row => {
                return(
                    <div className="text-right w-100">
                        {
                            usuario?.permisos?.clientes?.archivos?.eliminar &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarArchivo({ id_cliente: id, id_archivo: row._id, nombre: row.nombre })}><i className="fas fa-times text-danger"></i></button>
                        }
                        {
                            usuario?.permisos?.clientes?.archivos?.editar &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEditarArchivo({ id_cliente: id, id_archivo: row._id, ...row })}>
                                    <i className={`fas ${row.precargado ? 'fa-upload text-success' : 'fa-edit text-primary'}`}></i>
                                </button>
                        }
                        {
                            usuario?.permisos?.clientes?.archivos?.detalles && !row.precargado &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleDetallesArchivo({ id_cliente: id, id_archivo: row._id })}><i className="fas fa-eye text-info"></i></button>
                        }
                    </div>
                )
            }
        }
    ];

    const membresiasColumnas = [
        {
            name: 'Membresía',
            sortable: true, 
            selector: row => row.membresia?.nombre || 'Sin definir',
        },
        {
            name: 'Total',
            selector: row => row.total === 0 ? 'Gratuita' : formatNumber(row.total, 2, '$ ', '') + ' IVA inc.'
        },
        {
            name: 'Periodo',
            sortable: true, 
            selector: row => handleRangoFechas(row.fecha_inicio, row.fecha_fin),
            grow: 3
        },
        {
            name: 'Estado',
            sortable: true,
            sortFunction: sortTableStatusStatus,
            selector: row => {
                const badge = (() => {
                    switch(row.status){
                        case 0: return <Badge pill bg="success">Activa</Badge>;
                        case 1: return <Badge pill bg="warning">Vencida</Badge>;
                        case 2: return <Badge pill bg="danger">Cancelada</Badge>;
                        default: return null;
                    }
                })();
                return (
                    <div className="d-flex flex-column gap-1 py-1">
                        <h5 className="mb-0">{badge}</h5>
                        {row.cancel_at_period_end && <small><Badge pill bg="warning" text="dark">Sin renovación</Badge></small>}
                    </div>
                );
            },
        },
        {
            name: '',
            grow: 0.5, 
            cell: row => {
                return(
                    <div className="text-right w-100">
                        {
                            usuario?.permisos?.clientes?.membresia &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarMembresia({ id_cliente: id, id_membresia: row._id, nombre: row.membresia?.nombre, fecha: row.fecha_inicio })}><i className="fas fa-times text-danger"></i></button>
                        }
                    </div>
                )
            }
        }
    ];

    const cobrosColumnas = [
        {
            name: 'Fecha',
            sortable: true,
            sortFunction: sortTableDateFecha,
            cell: item => item.fecha ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'
        },
        {
            name: 'Total',
            selector: row => row.total === 0 ? 'Gratuita' : formatNumber(row.total, 2, '$ ', '') + ' IVA inc.'
        },
        {
            name: 'Tarjeta',
            selector: row => row.marca && row.digitos ? `${row.marca} **** ${row.digitos}` : 'Sin definir'
        },
        {
            name: 'Estado',
            sortable: true,
            selector: row => {
                switch(row.status){
                    case 2:
                        return <h5><Badge pill bg="success">Pagado</Badge></h5>;
                    case 3:
                        return <h5><Badge pill bg="danger">Fallido</Badge></h5>;
                    case 4:
                        return <h5><Badge pill bg="secondary">Cancelado</Badge></h5>;
                    default:
                        return <h5><Badge pill bg="warning">Pendiente</Badge></h5>;
                }
            }
        }
    ];

    /* Archivos */
    const handleAgregarArchivo = (cliente) => {
        setNuevoArchivo({
            cliente
        });
    }

    const handleDetallesArchivo = (archivo) => {
        dispatch(detallesArchivo(archivo));
    }

    const handleAbrirArchivo = () => {
        if(!editarArchivo){
            window.open(detalles_archivo.url, '_blank', 'noopener,noreferrer');
        }
    }

    const handleEditarArchivo = (cliente) => {
        setEditarArchivo(cliente);
    }

    const handleEliminarArchivo = (cliente) => {
        setEliminarArchivo(cliente);
    }

    /* Ahorros */
    const handleVerComprobanteAhorro = (ahorro) => {
        dispatch(detallesAhorro(ahorro));
    }

    /* Membresias */
    const handleEliminarMembresia = (membresia) => {
        setEliminarMembresia(membresia);
    }

    /* Funciones generales */
    const handleRangoFechas = (inicio, fin) => {
        if(inicio && fin){
            let diferencia = new Date(fin).getTime() - new Date(inicio).getTime();
            let total_dias = Math.ceil(diferencia / (1000 * 3600 * 24));

            if(total_dias <= 1){
                return 'Únicamente el ' + (new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }))
            }else{
                return 'Del ' + (new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })) + ' al ' + (new Date(fin).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }))
            }
        }else{
            return 'A partir del ' + (new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }));
        }
    }

    const handleStatus = (membresia) => {
        switch(membresia?.status){
            case 0:
                return(
                    <h3 className="text-muted"><Badge pill bg={membresia?.total === 0 ? 'info' : 'success'}>{membresia?.nombre}</Badge></h3>
                )
            case 1:
                return(
                    <h3 className="text-muted"><Badge pill bg="warning">Vencida</Badge></h3>
                )
            case 2:
                return(
                    <h3 className="text-muted"><Badge pill bg="danger">Cancelada</Badge></h3>
                )
            default:
                return(
                    <h3 className="text-muted"><Badge pill bg="secondary">Sin membresía</Badge></h3>
                )
        }
    }

    const handleGestionarMembresia = (membresia) => {
        if(membresia === null){
            setMembresiaActual(null);
        } else if(usuario?.permisos?.clientes?.membresia && membresia?.status === 0){
            const contratacion = membresias.find(c => String(c._id) === String(membresia._id));
            setMembresiaActual({ ...membresia, stripe_price_id: contratacion?.membresia?.stripe_price_id ?? null });
        }
    }

    useEffect(() => {
        dispatch(limpiarCliente());
        dispatch(limpiarArchivo());
        dispatch(limpiarAhorro());

        return () => {
            dispatch(limpiarCliente());
            dispatch(limpiarArchivo());
            dispatch(limpiarAhorro());
        }
    }, []);

    useEffect(() => {
        if(id){
            dispatch(obtenerCliente(id));

            rutasBread[1].path = rutasBread[1].path + id;

            dispatch(editarBreadcrumb(rutasBread));
        }
    }, [id]);

    useEffect(() => {
        if(detalles){
            setClienteData({
                ...clienteData,
                indicadores_generales: detalles.indicadores_generales, 
                informacion_personal: {
                    ...detalles.informacion_personal
                }, 
                informacion_empresarial: {
                    ...detalles.informacion_empresarial
                }, 
                archivos: detalles.archivos,
                asesorias: detalles.asesorias,
                ahorros: detalles.ahorros || [],
                membresias: detalles.contrataciones,
                cobros: detalles.cobros
            });
        }
    }, [detalles]);

    useEffect(() => {
        if(detalles_archivo && detalles_archivo?.extension && detalles_archivo?.url){
            handleAbrirArchivo();
        }
    }, [detalles_archivo]);

    useEffect(() => {
        if(detalles_ahorro?.url_comprobante){
            window.open(detalles_ahorro.url_comprobante, '_blank', 'noopener,noreferrer');
            dispatch(limpiarAhorro());
        }
    }, [detalles_ahorro]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .btn-badge-status{
                            background-color: #fff;
                            border: none;
                            outline: none;
                            padding: 0;
                        }

                        .valor_indicador {
                            font-size: 40px;
                            margin: 0;
                        }

                        .descripcion_indicador {
                            margin: 0;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>
            
            <GestionarMembresia membresia={membresiaActual} reestablecerMembresia={handleGestionarMembresia} />
            <EliminarMembresia membresia={eliminarMembresia} reestablecerMembresia={handleEliminarMembresia} />
            <NuevoArchivo archivo={nuevoArchivo} reestablecerArchivo={handleAgregarArchivo} />
            <EditarArchivo archivo={editarArchivo} reestablecerArchivo={handleEditarArchivo} />
            <EliminarArchivo archivo={eliminarArchivo} reestablecerArchivo={handleEliminarArchivo} />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Perfil del cliente</h3>
                    
                    <div className="row mb-4">
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.asesorias_totales, 0, '', '') }</p>
                            <p className="descripcion_indicador">Asesorías totales</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.asesorias_pendientes, 0, '', '') }</p>
                            <p className="descripcion_indicador">Asesorías en curso</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.asesorias_finalizadas, 0, '', '') }</p>
                            <p className="descripcion_indicador">Asesorías finalizadas</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.ahorro_generado || 0, 2, '$', '') }</p>
                            <p className="descripcion_indicador">Ahorro generado</p>
                        </div>
                        <div className="col-md text-center">
                            <div className="mt-2 d-flex justify-content-center flex-column align-items-center">
                                <button className="btn-badge-status" onClick={() => handleGestionarMembresia(indicadores_generales?.membresia_actual)}>
                                    { handleStatus(indicadores_generales?.membresia_actual) }
                                </button>
                                {indicadores_generales?.membresia_actual?.cancel_at_period_end &&
                                    <small className="mt-1"><Badge pill bg="warning" text="dark"><i className="fa-solid fa-ban me-1"></i>Sin renovación</Badge></small>
                                }
                            </div>
                            <p className="descripcion_indicador">Membresía</p>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-12">
                            <div className="accordion" id="accordionDetalles">
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingOne">
                                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                                            Información personal
                                        </button>
                                    </h2>
                                    <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-4">
                                                <label className="fw-bold">Nombre</label>
                                                <p>{[informacion_personal?.nombre, informacion_personal?.apellido_paterno, informacion_personal?.apellido_materno].join(' ')}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Celular</label>
                                                <p>{informacion_personal?.celular ? <>{informacion_personal.celular} <a href={`https://wa.me/${informacion_personal.celular.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"><i className="fab fa-whatsapp text-success"></i></a></> : 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Email</label>
                                                <p>{informacion_personal?.email || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Fecha de nacimiento</label>
                                                <p>{informacion_personal?.nacimiento ? new Date(informacion_personal?.nacimiento).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Fecha de registro</label>
                                                <p>{informacion_personal?.creacion ? new Date(informacion_personal?.creacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingTwo">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="true" aria-controls="collapseTwo">
                                            Información empresarial
                                        </button>
                                    </h2>
                                    <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-4">
                                                <label className="fw-bold">Nombre comercial</label>
                                                <p>{informacion_empresarial?.nombre_comercial || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Razón social</label>
                                                <p>{informacion_empresarial?.razon_social || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">RFC</label>
                                                <p>{informacion_empresarial?.rfc || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">CURP</label>
                                                <p>{informacion_empresarial?.curp || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Tipo de persona</label>
                                                <p>{informacion_empresarial?.tipo_persona || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Régimen fiscal</label>
                                                <p>{informacion_empresarial?.regimen_fiscal?.nombre || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Actividad empresarial</label>
                                                <p>{informacion_empresarial?.giro?.nombre || 'Sin definir'}</p>
                                            </div>
                                            
                                            <hr />

                                            <div className="col-md-4">
                                                <label className="fw-bold">Representante legal</label>
                                                <p>{informacion_empresarial?.representante_legal || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Puesto</label>
                                                <p>{informacion_empresarial?.puesto || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Domicilio fiscal</label>
                                                <p>{informacion_empresarial?.domicilio_fiscal || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Teléfono</label>
                                                <p>{informacion_empresarial?.telefono || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Correo</label>
                                                <p>{informacion_empresarial?.email || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Sitio web</label>
                                                <p>{informacion_empresarial?.web ? (<a target='_blank' href={informacion_empresarial?.web}>{informacion_empresarial?.web}</a>) : ('Sin definir')}</p>
                                            </div>

                                            <hr />

                                            <div className="col-md-4">
                                                <label className="fw-bold">Fecha de constitución</label>
                                                <p>{informacion_empresarial?.fecha_constitucion ? new Date(informacion_empresarial?.fecha_constitucion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Número de empleados</label>
                                                <p>{informacion_empresarial?.numero_empleados ? formatNumber(informacion_empresarial?.numero_empleados, 0, '', '') : 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Mínimo de ingresos anuales</label>
                                                <p>{informacion_empresarial?.minimo_ingresos_anuales ? formatNumber(informacion_empresarial?.minimo_ingresos_anuales, 0, '$', '') : 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Máximo de ingresos anuales</label>
                                                <p>{informacion_empresarial?.maximo_ingresos_anuales ? formatNumber(informacion_empresarial?.maximo_ingresos_anuales, 0, '$', '') : 'Sin definir'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingThree">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="true" aria-controls="collapseThree">
                                            Expediente de archivos
                                        </button>
                                    </h2>
                                    <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-12 text-end mb-3">
                                                {
                                                    usuario?.permisos?.clientes?.archivos?.agregar &&
                                                        <div className="btn-group" role="group" aria-label="Button group with nested dropdown">
                                                            <button type="button" className="btn btn-primary" onClick={() => handleAgregarArchivo(id)}><i className="fa-solid fa-plus"></i> Archivo</button>
                                                        </div>
                                                }
                                            </div>
                                            <DataTable
                                                columns={archivosColumnas}
                                                data={archivos}
                                                noDataComponent="No hay asesorías registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFour">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="true" aria-controls="collapseFour">
                                            Historial de asesorías
                                        </button>
                                    </h2>
                                    <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={asesoriasColumnas}
                                                data={asesorias}
                                                noDataComponent="No hay asesorías registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFive">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFive" aria-expanded="true" aria-controls="collapseFive">
                                            Historial de membresías
                                        </button>
                                    </h2>
                                    <div id="collapseFive" className="accordion-collapse collapse" aria-labelledby="headingFive" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={membresiasColumnas}
                                                data={membresias}
                                                noDataComponent="No hay contrataciones de membresías registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingSix">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSix" aria-expanded="true" aria-controls="collapseSix">
                                            Historial de pagos
                                        </button>
                                    </h2>
                                    <div id="collapseSix" className="accordion-collapse collapse" aria-labelledby="headingSix" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={cobrosColumnas}
                                                data={cobros}
                                                noDataComponent="No hay cobros registrados"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingSeven">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSeven" aria-expanded="true" aria-controls="collapseSeven">
                                            Ahorros generados
                                        </button>
                                    </h2>
                                    <div id="collapseSeven" className="accordion-collapse collapse" aria-labelledby="headingSeven" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={ahorrosColumnas}
                                                data={ahorros}
                                                noDataComponent="Aún no hay registros"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default DetallesCliente;